import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Box, Button, Typography, Card, CardContent } from "@mui/material";
import { ArrowBack, Preview } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Layout } from "../Layouts/Layout";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { extractErrorMessage } from "../utils/errorUtils";
import { GET_SUBMISSION_REVIEW_ACTIVITY } from "../Queries";
import { SubmissionReviewActivity, SubmissionReviewSubmission } from "../types";
import { AggregateResults } from "../Components/SubmissionReview/AggregateResults";
import { SubmissionDetailContent } from "../Components/SubmissionReview/SubmissionDetail";
import { CampaignProgress } from "../Components/Campaign/Progress";

/**
 * Instructor results with drill-down, mirroring the survey results pattern:
 *   /submissions/:activityId/results                 — activity overview
 *   /submissions/:activityId/results/:submissionId   — one student's detail
 * The student level links onward to the "preview as student" view.
 */
export function SubmissionReviewResultsPage() {
  const { activityId, submissionId } = useParams<{
    activityId: string;
    submissionId?: string;
  }>();
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { setError } = useErrorBoundary();

  const { data, loading, refetch } = useQuery(GET_SUBMISSION_REVIEW_ACTIVITY, {
    variables: { pk: activityId },
    onError: (error) =>
      setError(
        extractErrorMessage(error, String(t("Error loading results"))),
        error,
      ),
  });

  const activity: SubmissionReviewActivity | undefined =
    data?.submissionReviewActivity;
  const submissions = activity?.submissions ?? [];
  const isLive = activity?.submissionType === "LIV";

  const selected: SubmissionReviewSubmission | undefined = submissionId
    ? submissions.find((sub) => sub.id === submissionId)
    : undefined;

  const breadcrumbs = [
    { title: String(t("navigation.submissions")), link: "/submissions" },
    {
      title: activity?.name ?? "...",
      link: `/submissions/${activityId}`,
    },
    ...(selected
      ? [
          {
            title: String(t("Results")),
            link: `/submissions/${activityId}/results`,
          },
          {
            title: isLive
              ? selected.groupName ?? selected.authorName
              : selected.authorName,
          },
        ]
      : [{ title: String(t("Results")) }]),
  ];

  return (
    <Layout breadcrumbs={breadcrumbs} loading={loading && !activity}>
      {activity && !selected && (
        <>
          {/* ── Overview level ─────────────────────────────────────────── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="h5">
              {activity.name} — {t("Results")}
            </Typography>
            <Box sx={{ display: "flex", gap: 4 }}>
              {!isLive && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("Submissions")}
                  </Typography>
                  <CampaignProgress
                    submitted={activity.submittedCount ?? 0}
                    total={activity.submissionCount ?? 0}
                  />
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t("Reviews")}
                </Typography>
                <CampaignProgress
                  submitted={activity.completedReviews ?? 0}
                  total={activity.totalReviews ?? 0}
                />
              </Box>
            </Box>
          </Box>

          {submissions.length > 0 ? (
            <AggregateResults
              activity={activity}
              submissions={submissions}
              onStudentClick={(sub) =>
                navigate(`/submissions/${activityId}/results/${sub.id}`)
              }
            />
          ) : (
            <Card variant="outlined">
              <CardContent>
                <Typography color="text.secondary">
                  {t("No results yet — open the activity to get started.")}
                </Typography>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {activity && selected && (
        <>
          {/* ── Student level ──────────────────────────────────────────── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                component={Link}
                to={`/submissions/${activityId}/results`}
                startIcon={<ArrowBack />}
                size="small"
              >
                {t("All Results")}
              </Button>
              <Typography variant="h5">
                {isLive
                  ? selected.groupName ?? selected.authorName
                  : selected.authorName}
              </Typography>
            </Box>
            <Button
              component={Link}
              to={`/submissions/${activityId}/results/preview/${selected.authorId}`}
              startIcon={<Preview />}
              variant="outlined"
              size="small"
            >
              {t("Preview as Student")}
            </Button>
          </Box>
          <SubmissionDetailContent
            submission={selected}
            activity={activity}
            onRefresh={refetch}
          />
        </>
      )}
    </Layout>
  );
}
